/**
 * Device Replacement Modal
 * Modal สำหรับเปลี่ยนอุปกรณ์/SIM ในสัญญาเช่า
 *
 * รองรับ 3 โหมด:
 * - DEVICE_ONLY: เปลี่ยนเฉพาะอุปกรณ์
 * - SIM_ONLY: เปลี่ยนเฉพาะ SIM
 * - BOTH: เปลี่ยนทั้งอุปกรณ์และ SIM
 */
import React, { useState, useEffect, useRef } from 'react';
import { ArrowsRightLeftIcon, DevicePhoneMobileIcon, SignalIcon } from '@heroicons/react/24/outline';
import { RiAlertLine, RiExchangeLine, RiRemoteControlLine } from 'react-icons/ri';
import Modal from '../ui/Modal';
import SearchableSelect from '../ui/SearchableSelect';
import { useReplaceDevice } from '../../hooks/mutations/useDeviceReplacementMutations';
import deviceService from '../../services/deviceService';
import simService from '../../services/simService';
import {
  isSimSelectableForReplacement,
  excludeSimsPendingReturn,
  simPendingReturnReason,
  simIssuedReason,
} from '../../utils/simAvailability';
import { excludePendingReturn, pendingReturnReason } from '../../utils/devicePickerFilter';
import { rentalHoldFor, withRentalHoldLabel } from '../../utils/rentalHold';
import type { RentalHoldInfo } from '../../types/rentalHold';
import { getSimStatusLabel } from '../../utils/simHelpers';
import { statusColors } from '../../config/designSystem';
import { DeviceStatus } from '../../types/device';
import { searchKey } from '../../utils/thaiText';
import {
  ReplacementType,
  DeviceCondition,
  SimCondition,
  ReplacementIconType,
  REPLACEMENT_TYPES,
  DEVICE_CONDITIONS,
  SIM_CONDITIONS,
} from '../../services/deviceReplacementService';

// Icon component mapping for replacement types
const ReplacementTypeIcon: React.FC<{ iconType: ReplacementIconType; className?: string }> = ({ iconType, className = 'w-5 h-5' }) => {
  switch (iconType) {
    case 'device':
      return <DevicePhoneMobileIcon className={className} />;
    case 'sim':
      return <SignalIcon className={className} />;
    case 'both':
      return <RiExchangeLine className={className} />;
  }
};

interface RentalItem {
  id: number;
  device?: {
    id: number;
    code: string;
    serial_number: string;
    device_type?: { id: number; name: string };
    device_type_id?: number;
  };
  sim?: {
    id: number;
    phone_number: string;
    operator: string;
    expiry_date?: string;
  } | null;
  // caller บางจุด (เช่น GET /rentals/{id} รุ่นเก่า) ส่งมาแค่ sim_id ไม่มี sim object
  sim_id?: number | null;
  // AnyDesk ปัจจุบันของรายการเช่า (ของเครื่องเก่า) — ใช้แสดง hint ในช่อง AnyDesk เครื่องใหม่
  anydesk_id?: string | null;
}

interface DeviceReplacementModalProps {
  isOpen: boolean;
  onClose: () => void;
  rentalItem: RentalItem | null;
  onSuccess?: () => void;
  // เมื่อเปิด modal นี้จากหน้าเคสซ่อมบำรุง — ผูกการเปลี่ยนเข้ากับเคส + ตั้งค่าเริ่มต้นของ form
  maintenanceId?: number | null;
  defaultOldDeviceCondition?: DeviceCondition;
  defaultReason?: string;
}

interface DeviceOption {
  value: number;
  label: string;
}

interface SimOption {
  value: number;
  label: string;
}

const DeviceReplacementModal: React.FC<DeviceReplacementModalProps> = ({
  isOpen,
  onClose,
  rentalItem,
  onSuccess,
  maintenanceId,
  defaultOldDeviceCondition,
  defaultReason,
}) => {
  // Form state
  const [replacementType, setReplacementType] = useState<ReplacementType>('DEVICE_ONLY');
  const [newDeviceId, setNewDeviceId] = useState<number | ''>('');
  const [newSimId, setNewSimId] = useState<number | ''>('');
  const [oldDeviceCondition, setOldDeviceCondition] = useState<DeviceCondition>('DAMAGED');
  const [oldSimCondition, setOldSimCondition] = useState<SimCondition>('SIM_EXPIRED');
  const [reason, setReason] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  // AnyDesk ของเครื่องใหม่ (DEVICE_ONLY/BOTH) — เว้นว่างได้ backend จะล้างค่าเก่าของเครื่องเดิม
  const [newAnydeskId, setNewAnydeskId] = useState<string>('');

  // UI state
  const [availableDevices, setAvailableDevices] = useState<DeviceOption[]>([]);
  const [availableSims, setAvailableSims] = useState<SimOption[]>([]);
  // feedback Mr.Hop (Support) 2026-09-08: เพิ่มเครื่อง DEV151 เข้าระบบตอน 11:26 แล้วมาเปลี่ยน
  // อุปกรณ์ที่เคส #417 → ค้นหา S/N ของตัวเองไม่เจอ ขึ้น "ไม่พบข้อมูล" ลอย ๆ เสียเวลาเดาเอง
  // ~9 นาทีจนไปพบว่าเครื่องถูกบันทึกเป็นประเภท "อุปกรณ์เสริม" ส่วนเครื่องเดิมเป็น "เครื่องหลัก"
  //
  // ตัวกรอง (ประเภทเดียวกัน + ว่าง + มี S/N) **ถูกต้องตามเจตนา** — บั๊กคือ "กรองแล้วไม่บอก"
  // ของที่มีอยู่จริงแต่เลือกไม่ได้ ต้องบอกว่าทำไม ไม่ใช่เงียบ ไม่งั้นอ่านได้อย่างเดียวว่าระบบพัง
  //
  // ตัวกรองนี้แทบไม่เคยมีผลมาก่อนเลย เพราะ prod มี 827/829 เครื่องเป็น "เครื่องหลัก" ทั้งหมด
  // มันเพิ่งเริ่มกัดตอน Support สร้างของประเภทอื่นจากฟีเจอร์สต็อกใหม่ → จะเจอบ่อยขึ้นเรื่อย ๆ
  const [deviceEmptyReason, setDeviceEmptyReason] = useState<string>('');
  const [simEmptyReason, setSimEmptyReason] = useState<string>('');
  // ⚠️ `SearchableSelect` เข้าโหมด "server-search" ทันทีที่ได้รับ prop `onSearchChange`
  // (`filteredOptions = onSearchChange ? safeOptions : safeOptions.filter(...)`) = **ปิดการกรอง
  // ในเครื่อง** แล้วคาดหวังให้ตัวแม่เป็นคนส่ง options ที่กรองแล้วมาให้
  // เจอตอน E2E: พอเพิ่ม onSearchChange เข้าไปเฉย ๆ dropdown แสดงทุกตัวเลือกไม่ว่าพิมพ์อะไร
  // → เก็บคำค้นไว้กรองเองที่นี่ แทนการแก้ component กลางซึ่งมีผู้เรียกอื่นพึ่ง contract เดิมอยู่
  const [deviceSearch, setDeviceSearch] = useState<string>('');
  const [simSearch, setSimSearch] = useState<string>('');
  // ซิมดิบก่อนกรอง — ตอบได้เลยว่า "เบอร์นี้มีอยู่ แต่สถานะ X เลยเลือกไม่ได้" โดยไม่ต้องยิง API ซ้ำ
  const allSimsRef = useRef<Array<{ id: number; phone_number: string; operator?: string; status: string; rental_hold?: RentalHoldInfo | null }>>([]);
  // เครื่องดิบก่อนกรอง — fix ค: ต้องดึง rental_hold ของ "เครื่องที่เลือกไว้" มาโชว์เป็นคำเตือน
  // ใต้ช่อง (options เก็บแค่ {value, label} ทิ้ง rental_hold ไปแล้ว)
  const allDevicesRef = useRef<Array<{ id: number; rental_hold?: RentalHoldInfo | null }>>([]);
  const [loading, setLoading] = useState(false);
  // BUG-S805-DEVREPL-DBLSUBMIT: synchronous in-flight guard กัน rapid double-click ยิง mutateAsync ซ้ำ
  // (loading/isPending เป็น react-state → re-render ไม่ทันบล็อก click ที่ 2 same-tick · เดียวกับ BUG-S802/S803)
  const isSubmittingRef = useRef(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const replaceDeviceMutation = useReplaceDevice();

  // Check if rental item has SIM — รับทั้ง sim object และ sim_id เปล่า (BUG amintra 2026-08-26:
  // GET /rentals/{id} ส่งมาแค่ sim_id → ตัวเลือก "เปลี่ยน SIM" หายทั้งที่รายการมี SIM)
  const hasSim = !!(rentalItem?.sim || rentalItem?.sim_id);
  const hasDevice = !!rentalItem?.device;
  const showDeviceFields = replacementType === 'DEVICE_ONLY' || replacementType === 'BOTH';

  // ตัวสร้างตัวเลือก — ใช้ทั้งตอนโหลดชุดแรกและตอนค้นผ่าน server (BUG-S2207-001)
  const toDeviceOptions = (list: any[]) =>
    excludePendingReturn(list).map((d: any) => ({
      value: d.id,
      // fix ค: เครื่องที่ยังผูกกับสัญญาอื่นที่ยังไม่จบอยู่ (rental_hold) ต้องเห็นก่อนเลือก ไม่ใช่ซ่อนทิ้ง
      label: withRentalHoldLabel(`${d.code} - ${d.serial_number || 'ไม่มี S/N'}`, d),
    }));
  const toSimOptions = (list: any[]) =>
    excludeSimsPendingReturn(
      list.filter((s: { status: string }) => isSimSelectableForReplacement(s.status))
    ).map((s: any) => ({
      value: s.id,
      // fix ค: ซิมที่ยังผูกกับสัญญาอื่นที่ยังไม่จบอยู่ (rental_hold) ต้องเห็นก่อนเลือก ไม่ใช่ซ่อนทิ้ง
      label: withRentalHoldLabel(`${s.phone_number} (${s.operator || 'ไม่ระบุค่าย'})`, s),
    }));
  // BUG-S2207-001: ชุดแรกโหลดแค่ limit 1000 (เพดาน API) — dev มีเครื่องว่างประเภทเดียวกัน 1,267 เครื่อง
  // เครื่องที่ถูกตัดทิ้งค้นไม่เจอเลยแม้พิมพ์ถูก (กรองในเครื่องจากชุดที่โหลดมา) และขึ้น "พบ … แต่ไม่อยู่ในรายการ
  // ที่เลือกได้" · พอผู้ใช้พิมพ์ ≥2 ตัว ถาม server ด้วยตัวกรองชุดเดิม + คำค้น แล้วรวมเข้ารายการ
  const mergeById = <T extends { id: number }>(base: T[], extra: T[]) => {
    const seen = new Set(base.map((x) => x.id));
    return base.concat(extra.filter((x) => !seen.has(x.id)));
  };
  useEffect(() => {
    const q = deviceSearch.trim();
    const deviceTypeId = rentalItem?.device?.device_type_id || (rentalItem?.device as any)?.device_type?.id;
    if (!isOpen || q.length < 2 || !deviceTypeId) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res: any = await deviceService.getDevices({
          device_type_id: deviceTypeId, status: DeviceStatus.AVAILABLE, is_serialized: true, search: q, limit: 100,
        });
        if (cancelled) return;
        const found = (Array.isArray(res) ? res : res?.items || []) as any[];
        if (!found.length) return;
        allDevicesRef.current = mergeById(allDevicesRef.current as any[], found);
        setAvailableDevices(toDeviceOptions(allDevicesRef.current as any[]));
      } catch { /* ชุดแรกยังใช้ได้ */ }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceSearch, isOpen, rentalItem]);
  useEffect(() => {
    const q = simSearch.trim();
    if (!isOpen || q.length < 2) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res: any = await simService.getSims({ search: q, limit: 100 });
        if (cancelled) return;
        const found = (Array.isArray(res) ? res : res?.items || []) as any[];
        if (!found.length) return;
        allSimsRef.current = mergeById(allSimsRef.current as any[], found);
        setAvailableSims(toSimOptions(allSimsRef.current as any[]));
      } catch { /* ชุดแรกยังใช้ได้ */ }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [simSearch, isOpen]);

  // Load available devices (same device_type, status = AVAILABLE)
  useEffect(() => {
    const loadAvailableDevices = async () => {
      if (!rentalItem?.device?.device_type_id && !rentalItem?.device?.device_type?.id) return;

      try {
        const deviceTypeId = rentalItem.device?.device_type_id || rentalItem.device?.device_type?.id;
        // BUG-S592-AVAILCAP: เครื่องว่างทั้ง 685 ตัวเป็น device_type เดียวกันหมด ('เครื่องหลัก')
        // → limit=100 ตัดทิ้ง 585 ตัว ทำให้เลือกเครื่องทดแทนรหัสเก่าไม่ได้
        const devices = await deviceService.getDevices({
          device_type_id: deviceTypeId,
          status: DeviceStatus.AVAILABLE,
          // ของนับจำนวน (ไม่มี S/N) เปลี่ยนผ่านโฟลว์นี้ไม่ได้ — backend ตีกลับ 400 ตอนกดบันทึก
          // (โฟลว์ทำงานรายเครื่อง ไม่แตะ devices.quantity) · เดิมไม่กรอง จึงโผล่ให้เลือกแล้วไป
          // ตายตอน submit = dead-end · ที่ไม่มีใครเจอเพราะของนับจำนวนบน dev บังเอิญเป็นคนละ
          // device_type จึงโดน type filter บังไว้ (data assumption ไม่ใช่ guard)
          is_serialized: true,
          limit: 1000,
        });

        // Handle both paginated response (with items) and direct array response
        const devicesList = Array.isArray(devices) ? devices : (devices.items || []);
        allDevicesRef.current = devicesList as any[];
        // R4U-2: เครื่องที่ยังมีใบตามเก็บค้าง (ของอาจยังอยู่กับลูกค้า แม้สถานะ drift เป็น AVAILABLE)
        // ไม่เสนอ — parity กับ /device-replacements/search-devices ที่ backend ตัดออกอยู่แล้ว
        // ไม่งั้นเลือกได้แล้วไปโดน 400 ด่าน L2 ตอนบันทึก (dead-end แบบเดียวกับของนับจำนวน B4b)
        setAvailableDevices(toDeviceOptions(devicesList as any[]));
      } catch (error) {
        console.error('Error loading available devices:', error);
      }
    };

    if (isOpen && (replacementType === 'DEVICE_ONLY' || replacementType === 'BOTH')) {
      loadAvailableDevices();
    }
  }, [isOpen, rentalItem, replacementType]);

  // Load available SIMs
  useEffect(() => {
    const loadAvailableSims = async () => {
      try {
        // Get SIMs that are available to use (ACTIVE or INACTIVE but not in use)
        // BUG-S592-AVAILCAP: กรอง EXPIRED/SUSPENDED ฝั่ง client แต่โหลดแค่ 100 จาก 691
        const sims = await simService.getAllSims();

        // Handle both paginated response (with items) and direct array response
        const simsList = Array.isArray(sims) ? sims : (sims.items || []);
        allSimsRef.current = simsList;
        // user bug Tiwakorn 2026-08-30 (maintenance #309): เดิมกรองออกแค่ EXPIRED/SUSPENDED → SIM IN_USE
        // (681 เบอร์บน prod) โผล่ให้เลือก แล้ว backend ตอบ 400 "SIM ที่เลือกไม่พร้อมใช้งาน" ทุกครั้ง
        // → กรองด้วย allow-list เดียวกับ backend (AVAILABLE/ACTIVE/INACTIVE) ใน utils/simAvailability
        // Lane D (L2 ฝั่งซิม): ไฟล์นี้เคยเป็น "ไฟล์เดียว สองมาตรฐาน" — ฝั่งเครื่องกรองใบตามเก็บ
        // ด้วย excludePendingReturn แล้ว แต่ฝั่งซิมกรองแค่สถานะ ทั้งที่ด่าน L2 ตีกลับทั้งคู่
        setAvailableSims(toSimOptions(simsList as any[]));
      } catch (error) {
        console.error('Error loading available sims:', error);
      }
    };

    if (isOpen && (replacementType === 'SIM_ONLY' || replacementType === 'BOTH')) {
      loadAvailableSims();
    }
  }, [isOpen, replacementType]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Default to SIM_ONLY if rental has SIM but no device (unlikely but handle it)
      // Default to DEVICE_ONLY if rental has device but no SIM
      if (hasSim && !hasDevice) {
        setReplacementType('SIM_ONLY');
      } else {
        setReplacementType('DEVICE_ONLY');
      }
      setNewDeviceId('');
      setNewSimId('');
      setOldDeviceCondition(defaultOldDeviceCondition || 'DAMAGED');
      setOldSimCondition('SIM_EXPIRED');
      setReason(defaultReason || '');
      setNotes('');
      setNewAnydeskId('');
      setErrors({});
    }
  }, [isOpen, hasSim, hasDevice, defaultOldDeviceCondition, defaultReason]);

  // อธิบายว่าทำไมของที่ผู้ใช้ค้นหา "มีอยู่แต่เลือกไม่ได้" — แทนคำว่า "ไม่พบข้อมูล" ลอย ๆ
  // เทียบด้วย id เสมอ (เชื่อถือได้) ส่วน "ชื่อ" ใช้เฉพาะตอนที่ payload ส่งมาจริง —
  // `GET /rentals/{id}` ไม่ได้ส่ง device_type object มาด้วย จึงห้ามเดาชื่อแล้วเอาไปบอกผู้ใช้
  const oldDeviceTypeId =
    (rentalItem?.device as any)?.device_type_id ?? (rentalItem?.device as any)?.device_type?.id;
  const oldDeviceTypeName: string | undefined = (rentalItem?.device as any)?.device_type?.name;

  const explainMissingDevice = async (term: string) => {
    const q = term.trim();
    // สั้นเกินไปยังไม่ต้องอธิบาย (ผู้ใช้ยังพิมพ์ไม่จบ) — และกันยิง API ถี่เกินเหตุ
    if (q.length < 3) { setDeviceEmptyReason(''); return; }
    // ถ้ามีตัวเลือกที่ตรงอยู่แล้ว ไม่ต้องอธิบายอะไร
    if (availableDevices.some((o) => searchKey(o.label).includes(searchKey(q)))) {
      setDeviceEmptyReason(''); return;
    }
    try {
      const res: any = await deviceService.getDevices({ search: q, limit: 5 });
      const found = (Array.isArray(res) ? res : res?.items || [])[0];
      if (!found) {
        setDeviceEmptyReason(`ไม่พบ "${q}" ในระบบ — ตรวจรหัสอุปกรณ์ / S/N อีกครั้ง หรือเพิ่มอุปกรณ์เข้าระบบก่อน`);
        return;
      }
      const name = `${found.code || ''} ${found.serial_number ? `(${found.serial_number})` : ''}`.trim();
      // เรียงจากเหตุที่ผู้ใช้แก้ยากสุดไปง่ายสุด — ประเภทผิดคือเคสที่ทำให้เสียเวลามากที่สุด
      if (found.is_serialized === false) {
        setDeviceEmptyReason(`พบ ${name} แต่เลือกไม่ได้ — เป็นของนับจำนวน ซึ่งเปลี่ยนผ่านหน้านี้ไม่ได้`);
      } else if (oldDeviceTypeId && found.device_type_id && found.device_type_id !== oldDeviceTypeId) {
        const foundType = found.device_type?.name ? `"${found.device_type.name}"` : 'คนละประเภท';
        const oldPart = oldDeviceTypeName
          ? `ส่วนเครื่องเดิมเป็น "${oldDeviceTypeName}"`
          : 'ซึ่งไม่ตรงกับประเภทของเครื่องเดิม';
        setDeviceEmptyReason(
          `พบ ${name} แต่เลือกไม่ได้ — เป็นประเภท ${foundType} ${oldPart} · ` +
          `แก้ประเภทอุปกรณ์ที่หน้าข้อมูลอุปกรณ์ก่อน แล้วกลับมาใหม่`
        );
      } else if (pendingReturnReason(found, name)) {
        // R4U-2: สถานะอาจเป็น "พร้อมใช้งาน" อยู่ก็จริง แต่ใบตามเก็บยังไม่ปิด — บอกเหตุที่แท้จริงก่อนเช็คสถานะ
        setDeviceEmptyReason(pendingReturnReason(found, name) as string);
      } else if (found.status && found.status !== 'AVAILABLE') {
        setDeviceEmptyReason(`พบ ${name} แต่เลือกไม่ได้ — สถานะปัจจุบันคือ "${(statusColors.device as any)[found.status]?.label || found.status}" ต้องเป็น "พร้อมใช้งาน"`);
      } else {
        setDeviceEmptyReason(`พบ ${name} แต่ไม่อยู่ในรายการที่เลือกได้`);
      }
    } catch {
      setDeviceEmptyReason('');
    }
  };

  const explainMissingSim = (term: string) => {
    const q = term.trim();
    if (q.length < 3) { setSimEmptyReason(''); return; }
    if (availableSims.some((o) => o.label.includes(q))) { setSimEmptyReason(''); return; }
    const found = allSimsRef.current.find((x) => (x.phone_number || '').includes(q));
    if (!found) {
      setSimEmptyReason(`ไม่พบเบอร์ "${q}" ในระบบ — ตรวจเบอร์อีกครั้ง หรือเพิ่ม SIM เข้าระบบก่อน`);
      return;
    }
    // Lane D (L2): ซิมอาจสถานะ "พร้อมใช้งาน" อยู่ก็จริง แต่ใบตามเก็บยังไม่ปิด — บอกเหตุที่แท้จริง
    // ก่อนเช็คสถานะ (ลำดับเดียวกับฝั่งเครื่องใน explainMissingDevice)
    const pendingReason = simPendingReturnReason(found as any, found.phone_number);
    if (pendingReason) {
      setSimEmptyReason(pendingReason);
      return;
    }
    const issuedReason = simIssuedReason(found as any, found.phone_number);
    if (issuedReason) {
      setSimEmptyReason(issuedReason);
      return;
    }
    setSimEmptyReason(
      `พบ ${found.phone_number} แต่เลือกไม่ได้ — สถานะปัจจุบันคือ "${getSimStatusLabel(found.status)}" ` +
      `(เลือกได้เฉพาะซิมที่ยังไม่ถูกใช้งาน)`
    );
  };

  const _match = (label: string, term: string) =>
    !term.trim() || searchKey(label).includes(searchKey(term.trim()));
  const visibleDevices = availableDevices.filter((o) => _match(o.label, deviceSearch));
  const visibleSims = availableSims.filter((o) => _match(o.label, simSearch));

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // Validate device fields for DEVICE_ONLY or BOTH
    if (replacementType === 'DEVICE_ONLY' || replacementType === 'BOTH') {
      if (!newDeviceId) {
        newErrors.newDeviceId = 'กรุณาเลือกอุปกรณ์ใหม่';
      }
      if (!oldDeviceCondition) {
        newErrors.oldDeviceCondition = 'กรุณาเลือกสภาพอุปกรณ์เดิม';
      }
    }

    // Validate SIM fields for SIM_ONLY or BOTH
    if (replacementType === 'SIM_ONLY' || replacementType === 'BOTH') {
      if (!newSimId) {
        newErrors.newSimId = 'กรุณาเลือก SIM ใหม่';
      }
      if (!oldSimCondition) {
        newErrors.oldSimCondition = 'กรุณาเลือกสาเหตุการเปลี่ยน SIM';
      }
    }

    // Common validation
    if (!reason.trim()) {
      newErrors.reason = 'กรุณาระบุเหตุผลการเปลี่ยน';
    }

    // Scroll to first error + focus
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const firstErrorField = Object.keys(newErrors)[0];
      // Try to find element by name attribute first (for inputs/textareas)
      let errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      // If not found, try data-name attribute (for wrapper elements like SearchableSelect)
      if (!errorElement) {
        errorElement = document.querySelector(`[data-name="${firstErrorField}"]`);
      }
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Only focus if it's a focusable element
        if (errorElement instanceof HTMLInputElement || errorElement instanceof HTMLTextAreaElement) {
          errorElement.focus();
        }
      }
      return false;
    }

    setErrors(newErrors);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !rentalItem) return;
    if (isSubmittingRef.current) return; // BUG-S805: sync race-loser drop
    isSubmittingRef.current = true;

    setLoading(true);
    try {
      await replaceDeviceMutation.mutateAsync({
        rentalItemId: rentalItem.id,
        data: {
          replacement_type: replacementType,
          // Device fields
          new_device_id: (replacementType === 'DEVICE_ONLY' || replacementType === 'BOTH') ? newDeviceId as number : null,
          old_device_condition: (replacementType === 'DEVICE_ONLY' || replacementType === 'BOTH') ? oldDeviceCondition : null,
          // ISSUE-S164-REPLRACE FIX: ส่ง device ที่แสดงใน dialog เป็น optimistic-lock guard
          // ถ้าอีก request เปลี่ยนอุปกรณ์ไปก่อน (race) → backend คืน 409 กัน double-swap
          expected_old_device_id: (replacementType === 'DEVICE_ONLY' || replacementType === 'BOTH') ? (rentalItem.device?.id ?? null) : null,
          // SIM fields
          new_sim_id: (replacementType === 'SIM_ONLY' || replacementType === 'BOTH') ? newSimId as number : null,
          old_sim_condition: (replacementType === 'SIM_ONLY' || replacementType === 'BOTH') ? oldSimCondition : null,
          // AnyDesk เครื่องใหม่ — ส่งเฉพาะตอนเปลี่ยนเครื่อง · ค่าว่าง → null (backend ล้างค่าเก่า, PO decision)
          new_anydesk_id: showDeviceFields ? (newAnydeskId.trim() || null) : null,
          // Common
          reason: reason.trim(),
          notes: notes.trim() || null,
          // ผูกกับเคสซ่อมบำรุง — ส่งเฉพาะตอนเปิด modal นี้จากหน้าเคสซ่อม
          maintenance_id: maintenanceId ?? null,
        },
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error replacing device/SIM:', error);
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
    }
  };

  // Get available replacement types based on rental item
  const getAvailableReplacementTypes = () => {
    const types = [];
    if (hasDevice) {
      types.push(REPLACEMENT_TYPES.find(t => t.value === 'DEVICE_ONLY')!);
    }
    if (hasSim) {
      types.push(REPLACEMENT_TYPES.find(t => t.value === 'SIM_ONLY')!);
    }
    if (hasDevice && hasSim) {
      types.push(REPLACEMENT_TYPES.find(t => t.value === 'BOTH')!);
    }
    return types;
  };

  if (!rentalItem) return null;

  const availableTypes = getAvailableReplacementTypes();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="เปลี่ยนอุปกรณ์ / SIM"
      size="xl"
    >
      <div className="space-y-6">
        {/* ผูกกับเคสซ่อมบำรุง — แสดงเมื่อเปิด modal นี้จากหน้ารายละเอียดเคสซ่อม */}
        {maintenanceId != null && (
          <div className="flex items-start gap-2 p-3 bg-info-50 border border-info-200 rounded-lg text-sm text-info-800">
            <RiExchangeLine className="w-4 h-4 mt-0.5 flex-shrink-0 text-info-600" />
            <span>
              ผูกกับเคสซ่อมบำรุง <span className="font-semibold">#{maintenanceId}</span> — ระบบจะบันทึกว่าเคสนี้มีการเปลี่ยนอุปกรณ์
            </span>
          </div>
        )}

        {/* ข้อมูลปัจจุบัน */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
            <div className="w-8 h-8 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg flex items-center justify-center shadow-sm">
              <DevicePhoneMobileIcon className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-secondary-800">ข้อมูลปัจจุบัน</h3>
          </div>
          <div className="bg-secondary-50 rounded-lg p-4">

          {/* อุปกรณ์ปัจจุบัน */}
          {rentalItem.device && (
            <div className="mb-3">
              <label className="block text-xs text-secondary-500 mb-1">อุปกรณ์ปัจจุบัน</label>
              <div className="bg-white border border-secondary-200 rounded-md p-3">
                <div className="flex items-center">
                  <DevicePhoneMobileIcon className="w-5 h-5 mr-2 text-blue-500" />
                  <div>
                    <p className="font-medium text-secondary-900">
                      {rentalItem.device.code || 'ไม่ระบุ'}
                    </p>
                    <p className="text-sm text-secondary-500">
                      S/N: {rentalItem.device.serial_number || 'ไม่มี'}
                      {rentalItem.device.device_type?.name && ` • ${rentalItem.device.device_type.name}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* SIM ปัจจุบัน — แสดงเบอร์ถ้ามี sim object ไม่มีก็แสดง "SIM #<id>" */}
          {hasSim && (
            <div>
              <label className="block text-xs text-secondary-500 mb-1">SIM ปัจจุบัน</label>
              <div className="bg-white border border-secondary-200 rounded-md p-3">
                <div className="flex items-center">
                  <SignalIcon className="w-5 h-5 mr-2 text-green-500" />
                  <div>
                    <p className="font-medium text-secondary-900">
                      {rentalItem.sim?.phone_number || `SIM #${rentalItem.sim?.id ?? rentalItem.sim_id}`}
                    </p>
                    {rentalItem.sim && (
                      <p className="text-sm text-secondary-500">
                        {rentalItem.sim.operator || 'ไม่ระบุค่าย'}
                        {rentalItem.sim.expiry_date && ` • หมดอายุ ${new Date(rentalItem.sim.expiry_date).toLocaleDateString('th-TH')}`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* ประเภทการเปลี่ยน */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
            <div className="w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center shadow-sm">
              <RiExchangeLine className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-secondary-800">ประเภทการเปลี่ยน <span className="text-red-500">*</span></h3>
          </div>
          <div className="bg-amber-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {availableTypes.map((type) => (
              <label
                key={type.value}
                className={`
                  flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all
                  ${replacementType === type.value
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-secondary-200 bg-white hover:border-secondary-300'}
                `}
              >
                <input
                  type="radio"
                  name="replacementType"
                  value={type.value}
                  checked={replacementType === type.value}
                  onChange={(e) => setReplacementType(e.target.value as ReplacementType)}
                  className="sr-only"
                  disabled={loading}
                />
                <ReplacementTypeIcon iconType={type.iconType} className="w-5 h-5 mr-2" />
                <span className={`text-sm font-medium ${replacementType === type.value ? 'text-primary-700' : 'text-secondary-700'}`}>
                  {type.label}
                </span>
              </label>
            ))}
          </div>
          </div>
        </div>

        {/* Device Section - แสดงเมื่อ DEVICE_ONLY หรือ BOTH */}
        {(replacementType === 'DEVICE_ONLY' || replacementType === 'BOTH') && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
                <DevicePhoneMobileIcon className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">เปลี่ยนอุปกรณ์</h3>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">

            {/* เลือกอุปกรณ์ใหม่ */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                เลือกอุปกรณ์ใหม่ <span className="text-red-500">*</span>
              </label>
              <div data-name="newDeviceId">
                <SearchableSelect
                  options={visibleDevices}
                  value={newDeviceId}
                  onChange={(value) => {
                    setNewDeviceId(value as number);
                    if (errors.newDeviceId) {
                      setErrors(prev => ({ ...prev, newDeviceId: '' }));
                    }
                  }}
                  placeholder="เลือกอุปกรณ์..."
                  label="อุปกรณ์ใหม่"
                  disabled={loading}
                  onSearchChange={(t) => { setDeviceSearch(t); explainMissingDevice(t); }}
                  emptyMessage={deviceEmptyReason || undefined}
                />
              </div>
              {errors.newDeviceId && (
                <p role="alert" className="text-red-500 text-xs mt-1">{errors.newDeviceId}</p>
              )}
              <p className="text-xs text-secondary-600 mt-1">
                * แสดงเฉพาะอุปกรณ์ประเภทเดียวกันที่พร้อมใช้งาน
              </p>
              {/* fix ค: เครื่องใหม่ที่เลือกไว้ยังผูกกับสัญญาอื่นที่ยังไม่จบอยู่ — ข้อความไทยจาก backend ตรง ๆ */}
              {(() => {
                const hold = rentalHoldFor(allDevicesRef.current.find((d) => d.id === newDeviceId));
                if (!hold) return null;
                return (
                  <p className="mt-1 text-xs text-info-700 bg-info-50 border border-info-200 rounded px-2 py-1">
                    {hold.message}
                  </p>
                );
              })()}
            </div>

            {/* สภาพอุปกรณ์เดิม */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                สภาพอุปกรณ์เดิม <span className="text-red-500">*</span>
              </label>
              <div className="flex flex-wrap gap-3">
                {DEVICE_CONDITIONS.map((condition) => (
                  <label key={condition.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="oldDeviceCondition"
                      value={condition.value}
                      checked={oldDeviceCondition === condition.value}
                      onChange={(e) => {
                        setOldDeviceCondition(e.target.value as DeviceCondition);
                        if (errors.oldDeviceCondition) {
                          setErrors(prev => ({ ...prev, oldDeviceCondition: '' }));
                        }
                      }}
                      className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                      disabled={loading}
                      aria-invalid={Boolean(errors.oldDeviceCondition)}
                      aria-describedby={errors.oldDeviceCondition ? 'oldDeviceCondition-error' : undefined}
                    />
                    <span className="ml-2 text-sm text-secondary-700">{condition.label}</span>
                  </label>
                ))}
              </div>
              {errors.oldDeviceCondition && (
                <p id="oldDeviceCondition-error" className="text-red-500 text-xs mt-1">{errors.oldDeviceCondition}</p>
              )}
            </div>
            </div>
          </div>
        )}

        {/* AnyDesk ของเครื่องใหม่ — แสดงเมื่อ DEVICE_ONLY หรือ BOTH (user feedback amintra 2026-08-26:
            เดิมไม่มีช่องนี้ → AnyDesk ของรายการเช่าค้างเป็นของเครื่องเก่า ต้องไปแก้ที่หน้าแก้ไขการเช่าเอง) */}
        {showDeviceFields && (
          <div className="space-y-4" data-testid="anydesk-section">
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
                <RiRemoteControlLine className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">Remote Access (AnyDesk) ของเครื่องใหม่</h3>
            </div>
            <div className="bg-indigo-50 rounded-lg p-4">
              {/* PO 2026-09-07: ตัดช่อง "รหัสผ่าน AnyDesk" ออกทั้งระบบ — เหลือเฉพาะ AnyDesk ID ของเครื่องใหม่ */}
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label htmlFor="newAnydeskId" className="block text-sm font-medium text-secondary-700 mb-1">
                    AnyDesk ID
                  </label>
                  <input
                    id="newAnydeskId"
                    name="newAnydeskId"
                    type="text"
                    value={newAnydeskId}
                    onChange={(e) => setNewAnydeskId(e.target.value)}
                    maxLength={50}
                    className="input font-mono"
                    placeholder="เช่น 123 456 789"
                    autoComplete="off"
                    disabled={loading}
                  />
                  <p className="text-xs text-secondary-500 mt-1" data-testid="anydesk-current">
                    AnyDesk ปัจจุบันของรายการเช่า (เครื่องเดิม):{' '}
                    {rentalItem.anydesk_id ? (
                      <span className="font-mono">{rentalItem.anydesk_id}</span>
                    ) : (
                      <span className="italic">ยังไม่ได้กรอก</span>
                    )}
                  </p>
                </div>
              </div>
              <p className="text-xs text-secondary-600 mt-3">
                ไม่บังคับ — AnyDesk เป็นของเครื่อง ค่าเดิม (ของเครื่องเก่า) จะถูกแทนด้วยค่าที่กรอกตรงนี้
              </p>
              {/* 23 ก.ย. 2026: เตือน (ไม่บล็อก) — เว้นว่าง = ช่อง AnyDesk ของสัญญาจะว่างหลังเปลี่ยน */}
              {!newAnydeskId.trim() && (
                <div
                  className="mt-3 flex items-start gap-2 rounded-md border border-warning-200 bg-warning-50 p-2 text-xs text-warning-800"
                  data-testid="anydesk-blank-warning"
                  role="status"
                >
                  <RiAlertLine className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    เครื่องใหม่ยังไม่มี AnyDesk — ช่องนี้ของสัญญาจะว่าง (กรอกทีหลังได้ที่หน้ารายละเอียดการเช่า)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SIM Section - แสดงเมื่อ SIM_ONLY หรือ BOTH */}
        {(replacementType === 'SIM_ONLY' || replacementType === 'BOTH') && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-secondary-200">
              <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center shadow-sm">
                <SignalIcon className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-secondary-800">เปลี่ยน SIM</h3>
            </div>
            <div className="bg-green-50 rounded-lg p-4">

            {/* เลือก SIM ใหม่ */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-secondary-700 mb-1">
                เลือก SIM ใหม่ <span className="text-red-500">*</span>
              </label>
              <div data-name="newSimId">
                <SearchableSelect
                  options={visibleSims}
                  value={newSimId}
                  onChange={(value) => {
                    setNewSimId(value as number);
                    if (errors.newSimId) {
                      setErrors(prev => ({ ...prev, newSimId: '' }));
                    }
                  }}
                  placeholder="เลือก SIM..."
                  label="SIM ใหม่"
                  disabled={loading}
                  onSearchChange={(t) => { setSimSearch(t); explainMissingSim(t); }}
                  emptyMessage={simEmptyReason || undefined}
                />
              </div>
              {errors.newSimId && (
                <p role="alert" className="text-red-500 text-xs mt-1">{errors.newSimId}</p>
              )}
              {/* fix ค: ซิมใหม่ที่เลือกไว้ยังผูกกับสัญญาอื่นที่ยังไม่จบอยู่ — ข้อความไทยจาก backend ตรง ๆ */}
              {(() => {
                const hold = rentalHoldFor(allSimsRef.current.find((s) => s.id === newSimId));
                if (!hold) return null;
                return (
                  <p className="mt-1 text-xs text-info-700 bg-info-50 border border-info-200 rounded px-2 py-1">
                    {hold.message}
                  </p>
                );
              })()}
            </div>

            {/* สาเหตุการเปลี่ยน SIM */}
            <div>
              <label className="block text-sm font-medium text-secondary-700 mb-2">
                สาเหตุการเปลี่ยน SIM <span className="text-red-500">*</span>
              </label>
              <div className="space-y-2">
                {SIM_CONDITIONS.map((condition) => (
                  <label key={condition.value} className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="oldSimCondition"
                      value={condition.value}
                      checked={oldSimCondition === condition.value}
                      onChange={(e) => {
                        setOldSimCondition(e.target.value as SimCondition);
                        if (errors.oldSimCondition) {
                          setErrors(prev => ({ ...prev, oldSimCondition: '' }));
                        }
                      }}
                      className="w-4 h-4 text-green-600 focus:ring-green-500"
                      disabled={loading}
                      aria-invalid={Boolean(errors.oldSimCondition)}
                      aria-describedby={errors.oldSimCondition ? 'oldSimCondition-error' : undefined}
                    />
                    <span className="ml-2 text-sm text-secondary-700">{condition.label}</span>
                  </label>
                ))}
              </div>
              {errors.oldSimCondition && (
                <p id="oldSimCondition-error" className="text-red-500 text-xs mt-1">{errors.oldSimCondition}</p>
              )}
              {/* ทิวากร 2026-09-07: สาเหตุนี้ซิมเดิมไม่ได้เสีย — บอกให้ชัดว่าของจะกลับเข้าคลังเองตอนรับคืน
                  จะได้ไม่ต้องไปแก้สถานะซิมด้วยมือที่หน้าข้อมูล SIM */}
              {oldSimCondition === 'SIM_NORMAL_WITH_DEVICE' && (
                <p className="text-xs text-secondary-600 mt-2 bg-secondary-50 border border-secondary-200 rounded p-2">
                  ซิมเดิมจะถูกพักเป็น <strong>ไม่ได้ใช้งาน</strong> ไว้ก่อน (ของจริงยังอยู่ที่ลูกค้า)
                  แล้วกลับเป็น <strong>พร้อมใช้งาน</strong> อัตโนมัติเมื่อบันทึกรับคืนซิมนี้ในใบตามเก็บอุปกรณ์
                </p>
              )}
            </div>
            </div>
          </div>
        )}

        {/* Common Details */}
        <div>
          <h3 className="text-sm font-medium text-secondary-700 mb-3">รายละเอียด</h3>

          {/* เหตุผลการเปลี่ยน */}
          <div className="mb-4">
            <label htmlFor="replacementReason" className="block text-sm font-medium text-secondary-700 mb-1">
              เหตุผลการเปลี่ยน <span className="text-red-500">*</span>
            </label>
            <textarea
              id="replacementReason"
              name="reason"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errors.reason) {
                  setErrors(prev => ({ ...prev, reason: '' }));
                }
              }}
              rows={3}
              className={`input${errors.reason ? ' border-red-300' : ''}`}
              placeholder={
                replacementType === 'SIM_ONLY'
                  ? 'ระบุเหตุผลการเปลี่ยน เช่น SIM หมดอายุ, สัญญาณไม่ดี...'
                  : 'ระบุเหตุผลการเปลี่ยน เช่น หน้าจอไม่ติด, แบตเตอรี่เสื่อม...'
              }
              disabled={loading}
              aria-invalid={Boolean(errors.reason)}
              aria-describedby={errors.reason ? 'reason-error' : undefined}
            />
            {errors.reason && (
              <p id="reason-error" className="text-red-500 text-xs mt-1">{errors.reason}</p>
            )}
          </div>

          {/* หมายเหตุเพิ่มเติม */}
          <div>
            <label htmlFor="replacementNotes" className="block text-sm font-medium text-secondary-700 mb-1">
              หมายเหตุเพิ่มเติม
            </label>
            <textarea
              id="replacementNotes"
              name="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="input"
              placeholder="หมายเหตุเพิ่มเติม (ถ้ามี)"
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-md hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          disabled={loading}
        >
          ยกเลิก
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          disabled={loading || replaceDeviceMutation.isPending}
        >
          {(loading || replaceDeviceMutation.isPending) && (
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <ArrowsRightLeftIcon className="w-4 h-4 mr-1" />
          {replacementType === 'DEVICE_ONLY' && 'เปลี่ยนอุปกรณ์'}
          {replacementType === 'SIM_ONLY' && 'เปลี่ยน SIM'}
          {replacementType === 'BOTH' && 'เปลี่ยนอุปกรณ์และ SIM'}
        </button>
      </div>
    </Modal>
  );
};

export default DeviceReplacementModal;
